import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import faqService from '../../services/faqService';
import QuestionModal from './QuestionModal';
import './FAQEditorPage.css';

function FAQEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    icon: 'fas fa-question-circle',
    displayOrder: 0,
    isPublished: true,
  });
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const originalFormData = useRef(null);

  // Question management state
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);
  const [deleteQuestionModalOpen, setDeleteQuestionModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);

  useEffect(() => {
    if (isEditing) {
      loadSection();
    }
  }, [id, isEditing]);

  const loadSection = async () => {
    try {
      setIsLoading(true);
      const response = await faqService.getSection(id);
      const section = response.data;
      const loadedFormData = {
        slug: section.slug,
        title: section.title,
        icon: section.icon || 'fas fa-question-circle',
        displayOrder: section.displayOrder,
        isPublished: section.isPublished,
      };
      setFormData(loadedFormData);
      originalFormData.current = loadedFormData;
      setQuestions(section.questions || []);
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to load section');
      console.error('Error loading section:', error);
      navigate('/faq');
    } finally {
      setIsLoading(false);
    }
  };

  // Track unsaved changes
  const checkForChanges = useCallback((newFormData) => {
    if (!originalFormData.current) {
      // New section - check if any field has a value
      return newFormData.title.trim() !== '' || newFormData.slug.trim() !== '';
    }
    return JSON.stringify(newFormData) !== JSON.stringify(originalFormData.current);
  }, []);

  // Warn on page leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle escape key to close modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (deleteQuestionModalOpen && !isDeletingQuestion) {
          closeDeleteQuestionModal();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [deleteQuestionModalOpen, isDeletingQuestion]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData((prev) => {
      const newData = {
        ...prev,
        title,
        // Only auto-generate slug on create, not edit
        slug: isEditing ? prev.slug : generateSlug(title),
      };
      setHasUnsavedChanges(checkForChanges(newData));
      return newData;
    });
    // Clear title error when typing
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: null }));
    }
  };

  const handleSlugChange = (e) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData((prev) => {
      const newData = { ...prev, slug };
      setHasUnsavedChanges(checkForChanges(newData));
      return newData;
    });
    if (errors.slug) {
      setErrors((prev) => ({ ...prev, slug: null }));
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      setHasUnsavedChanges(checkForChanges(newData));
      return newData;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    // Slug validation
    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug must contain only lowercase letters, numbers, and hyphens';
    } else if (formData.slug.length > 50) {
      newErrors.slug = 'Slug must be 50 characters or less';
    }

    // Icon validation (optional but if provided, should be valid format)
    if (formData.icon && formData.icon.length > 100) {
      newErrors.icon = 'Icon class must be 100 characters or less';
    }

    // Display order validation
    if (formData.displayOrder < 0) {
      newErrors.displayOrder = 'Display order must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setIsSaving(true);

    try {
      if (isEditing) {
        // Update - don't send slug as it can't be changed
        await faqService.updateSection(id, {
          title: formData.title,
          icon: formData.icon || null,
          displayOrder: formData.displayOrder,
          isPublished: formData.isPublished,
        });
        toast.success('Section updated successfully');
        loadSection(); // Reload to get fresh data
      } else {
        // Create
        const response = await faqService.createSection({
          slug: formData.slug,
          title: formData.title,
          icon: formData.icon || null,
          displayOrder: formData.displayOrder,
          isPublished: formData.isPublished,
        });
        toast.success('Section created successfully');
        // Navigate to edit page for the new section
        navigate(`/faq/${response.data.id}/edit`, { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save section';
      toast.error(message);
      console.error('Error saving section:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Icon options with SVG icons for reliable rendering
  const iconOptions = [
    {
      value: 'fas fa-question-circle',
      label: 'Question',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" /></svg>
    },
    {
      value: 'fas fa-shopping-cart',
      label: 'Shopping',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" /></svg>
    },
    {
      value: 'fas fa-truck',
      label: 'Shipping',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg>
    },
    {
      value: 'fas fa-undo',
      label: 'Returns',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
    },
    {
      value: 'fas fa-box',
      label: 'Products',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" /></svg>
    },
    {
      value: 'fas fa-credit-card',
      label: 'Payment',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>
    },
    {
      value: 'fas fa-shield-alt',
      label: 'Security',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
    },
    {
      value: 'fas fa-user',
      label: 'Account',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
    },
    {
      value: 'fas fa-envelope',
      label: 'Contact',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
    },
    {
      value: 'fas fa-phone',
      label: 'Phone',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
    },
    {
      value: 'fas fa-map-marker-alt',
      label: 'Location',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
    },
    {
      value: 'fas fa-star',
      label: 'Featured',
      svg: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>
    },
  ];

  // Helper to get icon by value
  const getIconByValue = (value) => iconOptions.find(opt => opt.value === value);

  // Question handlers
  const openAddQuestionModal = () => {
    setSelectedQuestion(null);
    setQuestionModalOpen(true);
  };

  const openEditQuestionModal = (question) => {
    setSelectedQuestion(question);
    setQuestionModalOpen(true);
  };

  const closeQuestionModal = () => {
    if (!isSavingQuestion) {
      setQuestionModalOpen(false);
      setSelectedQuestion(null);
    }
  };

  const handleSaveQuestion = async (questionData) => {
    setIsSavingQuestion(true);

    try {
      if (questionData.id) {
        // Update existing question
        await faqService.updateQuestion(questionData.id, {
          question: questionData.question,
          answer: questionData.answer,
          displayOrder: questionData.displayOrder,
          isPublished: questionData.isPublished,
        });
        toast.success('Question updated successfully');
      } else {
        // Add new question
        await faqService.addQuestion(id, {
          question: questionData.question,
          answer: questionData.answer,
          displayOrder: questionData.displayOrder,
          isPublished: questionData.isPublished,
        });
        toast.success('Question added successfully');
      }

      // Reload section to get updated questions
      await loadSection();
      closeQuestionModal();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save question';
      toast.error(message);
      console.error('Error saving question:', error);
    } finally {
      setIsSavingQuestion(false);
    }
  };

  const confirmDeleteQuestion = (question) => {
    setQuestionToDelete(question);
    setDeleteQuestionModalOpen(true);
  };

  const closeDeleteQuestionModal = () => {
    if (!isDeletingQuestion) {
      setDeleteQuestionModalOpen(false);
      setQuestionToDelete(null);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;

    setIsDeletingQuestion(true);

    try {
      await faqService.deleteQuestion(questionToDelete.id);
      toast.success('Question deleted successfully');
      setQuestions(questions.filter((q) => q.id !== questionToDelete.id));
      closeDeleteQuestionModal();
    } catch (error) {
      toast.error('Failed to delete question');
      console.error('Error deleting question:', error);
    } finally {
      setIsDeletingQuestion(false);
    }
  };

  // Sort questions by display order
  const sortedQuestions = [...questions].sort((a, b) => a.displayOrder - b.displayOrder);

  if (isLoading) {
    return (
      <div className="faq-editor-page">
        {/* Skeleton Header */}
        <div className="page-header">
          <div className="skeleton skeleton-back-link"></div>
          <div className="skeleton skeleton-page-title"></div>
        </div>

        {/* Skeleton Tabs */}
        <div className="skeleton-tabs">
          <div className="skeleton skeleton-tab"></div>
          <div className="skeleton skeleton-tab"></div>
        </div>

        {/* Skeleton Form */}
        <div className="skeleton-form">
          <div className="skeleton-form-group">
            <div className="skeleton skeleton-label"></div>
            <div className="skeleton skeleton-input"></div>
          </div>
          <div className="skeleton-form-group">
            <div className="skeleton skeleton-label"></div>
            <div className="skeleton skeleton-input"></div>
            <div className="skeleton skeleton-help-text"></div>
          </div>
          <div className="skeleton-form-group">
            <div className="skeleton skeleton-label"></div>
            <div className="skeleton skeleton-input"></div>
          </div>
          <div className="skeleton-form-row">
            <div className="skeleton-form-group">
              <div className="skeleton skeleton-label"></div>
              <div className="skeleton skeleton-input"></div>
            </div>
            <div className="skeleton-form-group">
              <div className="skeleton skeleton-label"></div>
              <div className="skeleton skeleton-input"></div>
            </div>
          </div>
          <div className="skeleton-form-actions">
            <div className="skeleton skeleton-btn"></div>
            <div className="skeleton skeleton-btn" style={{ width: 140 }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="faq-editor-page">
      {/* Page Header */}
      <div className="page-header">
        <button onClick={() => navigate('/faq')} className="back-link">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to FAQ
        </button>
        <h1>{isEditing ? 'Edit Section' : 'New Section'}</h1>
        {isEditing && (
          <p className="section-slug">/{formData.slug}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          General
        </button>
        {isEditing && (
          <button
            className={`tab ${activeTab === 'questions' ? 'active' : ''}`}
            onClick={() => setActiveTab('questions')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
            </svg>
            Questions
            <span className="tab-badge">{questions.length}</span>
          </button>
        )}
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <form onSubmit={handleSubmit} className="editor-form">
          <div className="form-section">
            <h2>Section Details</h2>

            <div className="form-group">
              <label htmlFor="title">
                Title <span className="required">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="e.g., General Questions"
                className={errors.title ? 'error' : ''}
                maxLength={100}
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
              <span className="char-count">{formData.title.length}/100</span>
            </div>

            <div className="form-group">
              <label htmlFor="slug">
                Slug <span className="required">*</span>
              </label>
              <div className="slug-input-wrapper">
                <span className="slug-prefix">/faq/</span>
                <input
                  id="slug"
                  type="text"
                  value={formData.slug}
                  onChange={handleSlugChange}
                  placeholder="e.g., general-questions"
                  disabled={isEditing}
                  className={errors.slug ? 'error' : ''}
                  maxLength={50}
                />
              </div>
              {errors.slug && <span className="error-message">{errors.slug}</span>}
              {isEditing && (
                <span className="help-text">Slug cannot be changed after creation</span>
              )}
              {!isEditing && (
                <span className="help-text">URL-friendly identifier. Only lowercase letters, numbers, and hyphens.</span>
              )}
            </div>

            <div className="form-group">
              <label>Section Icon</label>
              <div className="icon-picker">
                <div className="icon-picker-grid">
                  {iconOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`icon-picker-item ${formData.icon === option.value ? 'active' : ''}`}
                      onClick={() => handleChange('icon', option.value)}
                    >
                      <span className="icon-picker-icon">{option.svg}</span>
                      <span className="icon-picker-label">{option.label}</span>
                    </button>
                  ))}
                </div>
                {formData.icon && getIconByValue(formData.icon) && (
                  <div className="icon-selected-preview">
                    <span className="icon-selected-icon">{getIconByValue(formData.icon).svg}</span>
                    <span className="icon-selected-text">
                      Selected: <strong>{getIconByValue(formData.icon).label}</strong>
                    </span>
                  </div>
                )}
              </div>
              {errors.icon && <span className="error-message">{errors.icon}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="displayOrder">Display Order</label>
                <input
                  id="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => handleChange('displayOrder', parseInt(e.target.value) || 0)}
                  min="0"
                  className={errors.displayOrder ? 'error' : ''}
                />
                {errors.displayOrder && <span className="error-message">{errors.displayOrder}</span>}
                <span className="help-text">Lower numbers appear first (0, 1, 2...)</span>
              </div>

              <div className="form-group">
                <label htmlFor="isPublished">Status</label>
                <div className="status-toggle">
                  <button
                    type="button"
                    className={`status-option ${formData.isPublished ? 'active published' : ''}`}
                    onClick={() => handleChange('isPublished', true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Published
                  </button>
                  <button
                    type="button"
                    className={`status-option ${!formData.isPublished ? 'active draft' : ''}`}
                    onClick={() => handleChange('isPublished', false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                    </svg>
                    Draft
                  </button>
                </div>
                <span className="help-text">
                  {formData.isPublished
                    ? 'Section is visible on the website'
                    : 'Section is hidden from the website'}
                </span>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/faq')}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="btn-spinner"></span>
                  Saving...
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Create Section'
              )}
            </button>
          </div>
        </form>
      )}

      {/* Questions Tab */}
      {activeTab === 'questions' && (
        <div className="questions-tab">
          <div className="questions-header">
            <h2>Questions ({questions.length})</h2>
            <button
              type="button"
              className="btn btn-primary"
              onClick={openAddQuestionModal}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="btn-icon">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Question
            </button>
          </div>

          {sortedQuestions.length > 0 ? (
            <div className="questions-list">
              {sortedQuestions.map((question, index) => (
                <div key={question.id} className="question-card">
                  <div className="question-header">
                    <div className="question-order">{index + 1}</div>
                    <div className="question-content">
                      <h3 className="question-text">{question.question}</h3>
                      <div
                        className="question-answer-preview"
                        dangerouslySetInnerHTML={{
                          __html: question.answer.replace(/<[^>]*>/g, ' ').substring(0, 150) + (question.answer.length > 150 ? '...' : '')
                        }}
                      />
                    </div>
                    <div className="question-meta">
                      <span className={`status-badge ${question.isPublished ? 'published' : 'draft'}`}>
                        {question.isPublished ? 'PUBLISHED' : 'DRAFT'}
                      </span>
                    </div>
                  </div>
                  <div className="question-actions">
                    <button
                      type="button"
                      className="btn-icon-action"
                      onClick={() => openEditQuestionModal(question)}
                      title="Edit question"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="btn-icon-action btn-danger"
                      onClick={() => confirmDeleteQuestion(question)}
                      title="Delete question"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="questions-empty">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="empty-icon">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
              </svg>
              <h3>No questions yet</h3>
              <p>Add your first question to this section</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={openAddQuestionModal}
              >
                Add Question
              </button>
            </div>
          )}
        </div>
      )}

      {/* Question Modal */}
      {questionModalOpen && (
        <QuestionModal
          question={selectedQuestion}
          onSave={handleSaveQuestion}
          onClose={closeQuestionModal}
          isSaving={isSavingQuestion}
        />
      )}

      {/* Delete Question Confirmation Modal */}
      {deleteQuestionModalOpen && (
        <div className="modal-overlay" onClick={closeDeleteQuestionModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="modal-warning-icon">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <h3>Delete Question</h3>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete this question?
              </p>
              <p className="modal-question-preview">
                "{questionToDelete?.question.substring(0, 100)}{questionToDelete?.question.length > 100 ? '...' : ''}"
              </p>
              <p className="modal-warning">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={closeDeleteQuestionModal}
                className="btn btn-secondary"
                disabled={isDeletingQuestion}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteQuestion}
                className="btn btn-danger"
                disabled={isDeletingQuestion}
              >
                {isDeletingQuestion ? 'Deleting...' : 'Delete Question'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FAQEditorPage;
